<template>
  <div class="sal-field">
    <label v-if="label" class="sal-form-label" :for="id">
      {{ label }}
    </label>

    <input
      v-if="type !== 'textarea'"
      :id="id"
      class="sal-input"
      :class="{ 'is-error': error }"
      :type="type"
      :inputmode="inputmode"
      :min="min"
      :max="max"
      :step="step"
      :placeholder="placeholder"
      :value="modelValue"
      :maxlength="maxlength"
      @input="handleInput"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    />

    <textarea
      v-else
      :id="id"
      class="sal-textarea"
      :class="{ 'is-error': error }"
      :placeholder="placeholder"
      :value="modelValue"
      :maxlength="maxlength"
      @input="handleInput"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    ></textarea>

    <p v-if="error && errorMessage" class="sal-error-message">
      {{ errorMessage }}
    </p>

    <p v-else-if="helper" class="sal-helper-text">
      {{ helper }}
    </p>

    <p v-if="showCounter && maxlength" class="sal-counter">
      {{ currentLength }}/{{ maxlength }} caracteres
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  id: {
    type: String,
    default: undefined
  },
  label: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  inputmode: {
    type: String,
    default: undefined
  },
  placeholder: {
    type: String,
    default: ''
  },
  min: {
    type: [String, Number],
    default: undefined
  },
  max: {
    type: [String, Number],
    default: undefined
  },
  step: {
    type: [String, Number],
    default: undefined
  },
  maxlength: {
    type: [String, Number],
    default: undefined
  },
  maxDigits: {
    type: [String, Number],
    default: undefined
  },
  helper: {
    type: String,
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  showCounter: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

const currentLength = computed(() => {
  return String(props.modelValue || '').length
})

function handleInput(event) {
  const value = props.maxDigits
    ? limitDigits(event.target.value, Number(props.maxDigits))
    : event.target.value

  if (value !== event.target.value) {
    event.target.value = value
  }

  emit('update:modelValue', value)
}

function limitDigits(value, maxDigits) {
  if (!Number.isFinite(maxDigits) || maxDigits <= 0) return value

  const raw = String(value ?? '').replace(/[^\d.]/g, '')
  const [whole = '', ...decimalParts] = raw.split('.')
  const decimal = decimalParts.join('').replace(/\D/g, '')
  const digits = `${whole}${decimal}`.slice(0, maxDigits)

  if (!digits) return ''
  if (!raw.includes('.')) return digits

  const wholeLength = Math.min(whole.length, digits.length)
  const limitedWhole = digits.slice(0, wholeLength)
  const limitedDecimal = digits.slice(wholeLength)

  return limitedDecimal ? `${limitedWhole || '0'}.${limitedDecimal}` : limitedWhole
}
</script>
